import { createContext, Dispatch, PropsWithChildren, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { AppContext } from "Context/AppContext";
import { getCategoryList } from "Components/GMS/CatalogManagement/service";
import { fetchAttributes } from "../services/CSServices";
import Utils from "Common/Utils";
import { getIterationMethod } from "../services/IterationServices";
import { toast } from "react-toastify";

export type IterationContextType = {
    industryId: string | null | undefined;
    channelKey: string;
    activeTab: ComputedSubstitutionTabType;
    onTabChange: (tab: ComputedSubstitutionTabType) => void;
    fetchInitialState: (uid?: string | undefined, categoryId?: string | undefined) => Promise<void>;
    resetState: () => void;
    allCategories: TCategory[] | undefined;
    handleCategoryChange: (e: string) => void;
    iterationPayload: CategoryData | undefined;
    setIterationPayload: Dispatch<SetStateAction<CategoryData | undefined>>;
    isEditMode: boolean | undefined;
    loading: boolean;
    createPermission: boolean;
    readPermission: boolean;
    updatePermission: boolean;
    deletePermission: boolean;

    // Properties for cancel functionality
    originalIterationPayload: CategoryData | null;
    hasUnsavedChanges: boolean;
    showCancelDialog: boolean;
    setShowCancelDialog: (show: boolean) => void;

    // Iteration management
    activeIterationId: string | null;
    currentIteration: Iteration | null
    activeIterationTab: IterationTabType;
    addIteration: () => void;
    removeIteration: (id: string) => void;
    copyIteration: (iterationId: string) => void;
    renameIteration: (iterationId: string, newName: number) => void;
    setActiveIterationId: (id: string) => void;
    setActiveIterationTab: (tab: IterationTabType) => void;
    reorderIterations: (sourceIndex: number, destinationIndex: number) => void;
    attributeList: any[];

    // Condition management
    addCondition: (iterationId: string, parentId?: string) => void;
    updateCondition: (iterationId: string, conditionId: string, updates: Partial<OperationCondition>) => void;
    removeCondition: (iterationId: string, conditionId: string) => void;
    reorderConditions: (iterationId: string, sourceIndex: number, destinationIndex: number) => void;

    // Filter management
    addFilter: (iterationId: string, conditionId: string, filter: Partial<Filter>) => void;
    updateFilter: (iterationId: string, conditionId: string, filterId: string, updates: Partial<Filter>) => void;
    removeFilter: (iterationId: string, conditionId: string, filterId: string) => void;

    // Indentation control
    indentCondition: (iterationId: string, conditionId: string, direction: 'left' | 'right') => void;

    // Logical operator management
    updateLogicalOperator: (
        iterationId: string,
        conditionId: string,
        operator: LogicalOperator
    ) => void;

    flattenConditions: (conditions: ConditionNode[]) => OperationCondition[];
};

export const IterationContext = createContext<IterationContextType>({} as IterationContextType);

export const IterationContextProvider = ({ children }: PropsWithChildren) => {
    const appContext = useContext(AppContext);
    const industryId: string | null | undefined = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
    const channelKey: string = appContext?.channelKey;
    const [activeTab, setActiveTab] = useState<ComputedSubstitutionTabType>('iteration');
    const [allCategories, setAllCategories] = useState<TCategory[] | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [attributeList, setAttributeList] = useState<any>([]);
    const [iterationPayload, setIterationPayload] = useState<CategoryData | undefined>(undefined);
    const [isEditMode, setIsEditMode] = useState<boolean | undefined>(undefined);

    const [originalIterationPayload, setOriginalIterationPayload] = useState<CategoryData | null>(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const [requestedIterationId, setRequestedIterationId] = useState<string | null>(null);
    const [activeIterationTab, setActiveIterationTab] = useState<IterationTabType>('matchingConditions');

    const createPermission = appContext.pm.checkPermission("gmssubstitution_create");
    const readPermission = appContext.pm.checkPermission("gmssubstitution_read");
    const updatePermission = appContext.pm.checkPermission("gmssubstitution_update");
    const deletePermission = appContext.pm.checkPermission("gmssubstitution_delete");

    // Helper function to check if any operation conditions have meaningful data
    const hasOperationConditionData = (conditions: ConditionNode[]): boolean => {
        for (const condition of conditions) {
            if (condition.type === 'operations') {
                const opCondition = condition as OperationCondition;
                if (opCondition.attribute_id || opCondition.filters?.length > 0) {
                    return true;
                }
            } else if (condition.type === 'condition') {
                const nestedCondition = condition as NestedCondition;
                if (hasOperationConditionData(nestedCondition.conditions)) {
                    return true;
                }
            }
        }
        return false;
    };

    // Add change detection logic
    const hasUnsavedChanges = useMemo(() => {
        if (isEditMode === false) {
            // Create mode: check if user has made meaningful changes
            return !!(
                iterationPayload?.category_id ||
                (iterationPayload?.iterations?.some(iteration =>
                    hasOperationConditionData(iteration.conditions)
                ))
            );
        } else if (isEditMode === true && originalIterationPayload) {
            // Edit mode: compare against original
            return JSON.stringify(originalIterationPayload) !== JSON.stringify(iterationPayload);
        }
        return false;
    }, [iterationPayload, originalIterationPayload, isEditMode]);

    const onTabChange = useCallback((tab: ComputedSubstitutionTabType) => {
        setActiveTab(tab);
    }, []);

    const activeIterationId = useMemo(() => {
        const iterations = iterationPayload?.iterations || [];

        if (iterations.length === 0) {
            return null;
        }

        if (requestedIterationId && iterations.some(it => it.id === requestedIterationId)) {
            return requestedIterationId;
        }
        return iterations[0].id;
    }, [requestedIterationId, iterationPayload?.iterations]);

    const currentIteration = useMemo(() => {
        if (!activeIterationId || !iterationPayload?.iterations) {
            return null;
        }
        return iterationPayload.iterations.find(it => it.id === activeIterationId) || null;
    }, [activeIterationId, iterationPayload?.iterations]);

    const setActiveIterationId = useCallback((id: string | null) => {
        setRequestedIterationId(id);
    }, []);

    const generateId = () => Utils.generate_guid();

    // Helper function to update priorities for outer conditions
    const updateOuterConditionPriorities = (conditions: ConditionNode[]): ConditionNode[] => {
        return conditions.map((condition, index) => ({
            ...condition,
            priority: index + 1
        }));
    };

    const createDefaultIteration = () => {
        const emptyCondition: OperationCondition = {
            id: generateId(),
            type: 'operations',
            priority: 1,
            attribute_id: "",
            attribute_name: "",
            condition: "==",
            value_type: "Absolute",
            operator: "",
            operand: "",
            prefix: "",
            suffix: "",
            logical_operator: "AND",
            filters: []
        };

        return {
            id: generateId(),
            name: 1,
            priority: 1,
            conditions: [emptyCondition],
            attribute_sorting: []
        };
    };

    const handleCategoryChange = (categoryId: string) => {
        if (!channelKey || !industryId) return
        // Find the category name from allCategories
        const selectedCategory = allCategories?.find(cat => cat.id === categoryId);

        // Create iteration with an empty condition
        const newIterationId = generateId();
        const emptyCondition: OperationCondition = {
            id: generateId(),
            type: 'operations',
            priority: 1,
            attribute_id: "",
            attribute_name: "",
            condition: "==",
            value_type: "Absolute",
            operator: "",
            operand: "",
            prefix: "",
            suffix: "",
            logical_operator: "AND",
            filters: []
        };

        const newIteration = {
            id: newIterationId,
            name: 1,
            priority: 1,
            conditions: [emptyCondition],
            attribute_sorting: []
        };

        setIterationPayload(prev => {
            const basePayload = prev || {
                id: generateId(),
                channel_key: channelKey,
                industry_id: industryId,
                category_name: selectedCategory?.name || '',
                category_id: categoryId,
                iterations: []
            };

            return {
                ...basePayload,
                category_id: categoryId,
                category_name: selectedCategory?.name || '',
                iterations: [newIteration]
            };
        });

        setActiveIterationId(newIterationId);

        getAttributes(categoryId);
    };

    async function getAttributes(categoryId: string) {
        try {
            let res = await fetchAttributes(categoryId);
            if (res) {
                const sortedData = res?.sort((a: any, b: any) => {
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                });
                const optionList = sortedData?.map((x: any) => {
                    return {
                        label: x?.name,
                        value: x?.id,
                        dataType: x?.datatype
                    };
                });
                setAttributeList(optionList);
            }
        } catch (error) {
            console.error("Error fetching attributes:", error);
            setAttributeList([]);
        }
    }

    const fetchInitialState = async (uid?: string | undefined, categoryId?: string | undefined) => {
        if (!channelKey || !industryId) return;

        try {
            setLoading(true);
            await getGMSCategories();

            // Determine if we're in edit mode based on uid presence
            const editMode = !!uid;
            setIsEditMode(editMode);

            if (editMode && uid) {
                // EDIT MODE: Fetch existing iteration method
                const data = await getIterationMethod(uid);

                if (data) {
                    // Ensure iterations array exists and has at least one iteration
                    const updatedData = {
                        ...data,
                        iterations: data.iterations && data.iterations.length > 0
                            ? data.iterations.map((iteration: any, index: number) => ({
                                ...iteration,
                                priority: index + 1,
                                conditions: updateOuterConditionPriorities(iteration.conditions || [])
                            }))
                            : [createDefaultIteration()]
                    };

                    setIterationPayload(updatedData);
                    // Store original for change detection
                    setOriginalIterationPayload(structuredClone(updatedData));

                    // Set active iteration to first one
                    if (updatedData.iterations.length > 0) {
                        setActiveIterationId(updatedData.iterations[0].id);
                    }

                    // Fetch attributes for the category
                    if (data.category_id) {
                        await getAttributes(data.category_id);
                    }
                }
            } else if (categoryId) {
                // CREATE MODE with pre-selected category
                const selectedCategory = allCategories?.find(cat => cat.id === categoryId);

                const newPayload: CategoryData = {
                    id: generateId(),
                    channel_key: channelKey,
                    industry_id: industryId,
                    category_name: selectedCategory?.name || '',
                    category_id: categoryId,
                    iterations: [createDefaultIteration()]
                };

                setIterationPayload(newPayload);
                // In create mode, original starts as null
                setOriginalIterationPayload(null);

                // Set the first iteration as active
                if (newPayload.iterations.length > 0) {
                    setActiveIterationId(newPayload.iterations[0].id);
                }

                await getAttributes(categoryId);
            }

        } catch (error) {
            console.error("ERROR in fetchInitialState:", error);
        } finally {
            setLoading(false);
        }
    };

    const resetState = () => {
        if (!channelKey || !industryId) return
        const defaultIteration = createDefaultIteration();

        setIterationPayload(prev => {
            if (!prev) {
                // If no previous payload, create a minimal one
                return {
                    id: generateId(),
                    channel_key: channelKey,
                    industry_id: industryId,
                    category_name: '',
                    category_id: '',
                    iterations: [defaultIteration]
                };
            }

            return {
                ...prev,
                iterations: [defaultIteration]
            };
        });

        setActiveIterationId(defaultIteration.id);
        setActiveIterationTab('matchingConditions');
    };

    const getGMSCategories = async () => {
        try {
            const response = await getCategoryList(industryId);
            if (response) {
                const sortedData = response.sort((a: any, b: any) => {
                    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
                });
                const option = sortedData?.map((x: any) => {
                    return {
                        name: x?.name,
                        id: x?.id,
                    };
                });
                setAllCategories(option);
            } else {
                setAllCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setAllCategories([]);
        }
    };

    // Helper function to flatten the hierarchical condition structure for UI rendering
    const flattenConditions = useCallback((conditions: ConditionNode[]): OperationCondition[] => {
        const result: OperationCondition[] = [];

        const traverse = (nodes: ConditionNode[], level = 0, parentId?: string) => {
            nodes.forEach(node => {
                if (node.type === 'operations') {
                    result.push({
                        ...node,
                        priority: result.length + 1
                    });
                } else if (node.type === 'condition' && node.conditions) {
                    traverse(node.conditions, level + 1, node.id);
                }
            });
        };

        traverse(conditions);
        return result;
    }, []);

    // Function to find a condition by ID in the nested structure
    const findCondition = (conditions: ConditionNode[], id: string): [ConditionNode | null, ConditionNode[] | null, number] => {
        for (let i = 0; i < conditions.length; i++) {
            if (conditions[i].id === id) {
                return [conditions[i], conditions, i];
            }

            if (conditions[i].type === 'condition') {
                const nestedCondition = conditions[i] as NestedCondition;
                if (nestedCondition.conditions) {
                    const [found, parent, index] = findCondition(nestedCondition.conditions, id);
                    if (found) {
                        return [found, parent, index];
                    }
                }
            }
        }

        return [null, null, -1];
    };

    // Add a new iteration
    const addIteration = useCallback(() => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iterations = prev.iterations || [];

            // Find the highest iteration number
            const getHighestIterationNumber = (iterations: Iteration[]): number => {
                if (iterations.length === 0) return 0;
                return Math.max(...iterations.map(iteration => iteration.name));
            };

            const highestNumber = getHighestIterationNumber(iterations);
            const newNumber = highestNumber + 1;
            const newId = generateId();

            const newIteration = {
                id: newId,
                name: newNumber,
                priority: iterations.length + 1,
                conditions: [],
                attribute_sorting: []
            };

            setActiveIterationId(newId);

            return {
                ...prev,
                iterations: [...iterations, newIteration]
            };
        });
    }, []);

    const removeIteration = useCallback((id: string) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const filteredIterations = prev.iterations.filter(iteration => iteration.id !== id);
            const reorderedIterations = filteredIterations.map((iteration, index) => ({
                ...iteration,
                priority: index + 1
            }));

            return {
                ...prev,
                iterations: reorderedIterations
            };
        });

    }, []);

    const copyIteration = useCallback((iterationId: string) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iterationToCopy = prev.iterations.find(it => it.id === iterationId);
            if (!iterationToCopy) {
                console.error('Iteration to copy not found:', iterationId);
                return prev;
            }

            // Find highest number for the copy
            const highestNumber = prev.iterations.length === 0
                ? 0
                : Math.max(...prev.iterations.map(iteration => iteration.name));

            const generateNewIdsRecursively = (conditions: ConditionNode[]): ConditionNode[] => {
                return conditions.map(condition => {
                    const newCondition = {
                        ...condition,
                        id: generateId()
                    };

                    // If it's a nested condition, recursively generate new IDs for sub-conditions
                    if (condition.type === 'condition') {
                        const nestedCondition = newCondition as NestedCondition;
                        nestedCondition.conditions = generateNewIdsRecursively(nestedCondition.conditions);
                    }

                    return newCondition;
                });
            };

            // Deep clone and create new iteration
            const newId = generateId();
            const clonedIteration = JSON.parse(JSON.stringify(iterationToCopy));
            const newIteration: Iteration = {
                ...clonedIteration,
                id: newId,
                name: highestNumber + 1,
                priority: prev.iterations.length + 1,
                conditions: updateOuterConditionPriorities(generateNewIdsRecursively(clonedIteration.conditions))
            };

            const updatedIterations = prev.iterations.map((iteration, index) => ({
                ...iteration,
                priority: index + 1
            }));
            setTimeout(() => setActiveIterationId(newId), 0);
            return {
                ...prev,
                iterations: [...updatedIterations, newIteration]
            };
        });
    }, [generateId]);

    const renameIteration = useCallback((iterationId: string, newName: number) => {
        if (!newName?.toString().trim()) {
            toast.warn('Cannot rename iteration to empty name');
            return;
        }

        setIterationPayload(prev => {
            if (!prev) return prev;

            const iterationExists = prev.iterations.some(it => it.id === iterationId);
            if (!iterationExists) {
                toast.error('Iteration to rename not found');
                return prev;
            }

            const updatedIterations = prev.iterations.map(iteration =>
                iteration.id === iterationId
                    ? { ...iteration, name: Number(newName?.toString().trim()) }
                    : iteration
            );

            return {
                ...prev,
                iterations: updatedIterations
            };
        });
    }, []);

    // Reorder iterations
    const reorderIterations = useCallback((sourceIndex: number, destinationIndex: number) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iterations = [...prev.iterations];
            const [removed] = iterations.splice(sourceIndex, 1);
            iterations.splice(destinationIndex, 0, removed);

            // Update priority and names after reordering
            const updatedIterations = iterations.map((iteration, index) => ({
                ...iteration,
                priority: index + 1
            }));

            return {
                ...prev,
                iterations: updatedIterations
            };
        });
    }, []);

    // Add a new condition
    const addCondition = useCallback((iterationId: string, parentId?: string) => {

        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            // Determine logical operator based on context
            const getLogicalOperatorForContext = (isNested: boolean): LogicalOperator => {
                return isNested ? "OR" : "AND";
            };

            // If parent ID is provided, add as a nested condition
            if (parentId) {
                const [parent, parentArray, parentIndex] = findCondition(iteration.conditions, parentId);

                if (parent && parent.type === 'condition' && parentArray) {
                    // Adding to existing nested condition - use OR
                    const nestedParent = parent as NestedCondition;
                    const newCondition: OperationCondition = {
                        id: generateId(),
                        type: 'operations',
                        attribute_id: "",
                        attribute_name: "",
                        condition: "==",
                        value_type: "Absolute",
                        operator: "",
                        operand: "",
                        prefix: "",
                        suffix: "",
                        logical_operator: getLogicalOperatorForContext(true),
                        filters: []
                    };

                    nestedParent.conditions.push(newCondition);

                    return { ...prev };
                } else if (parent && parent.type === 'operations' && parentArray) {
                    // Converting operations to nested condition - use OR for both children
                    const operationsParent = parent as OperationCondition;

                    const firstChild: OperationCondition = {
                        ...operationsParent,
                        // Remove priority from inner condition
                        priority: undefined,
                        logical_operator: getLogicalOperatorForContext(true)
                    };

                    const secondChild: OperationCondition = {
                        id: generateId(),
                        type: 'operations',
                        attribute_id: "",
                        attribute_name: "",
                        condition: "==",
                        value_type: "Absolute",
                        operator: "",
                        operand: "",
                        prefix: "",
                        suffix: "",
                        logical_operator: getLogicalOperatorForContext(true),
                        filters: []
                    };

                    const nestedCondition: NestedCondition = {
                        id: generateId(),
                        type: 'condition',
                        priority: operationsParent.priority,
                        logical_operator: operationsParent.logical_operator,
                        conditions: [firstChild, secondChild]
                    };

                    // Replace the original operations with the new nested condition
                    parentArray[parentIndex] = nestedCondition;

                    return { ...prev };
                }
            }

            // Add as a top-level condition - use AND
            const newCondition: OperationCondition = {
                id: generateId(),
                type: 'operations',
                priority: iteration.conditions.length + 1,
                attribute_id: "",
                attribute_name: "",
                condition: "==",
                value_type: "Absolute",
                operator: "",
                operand: "",
                prefix: "",
                suffix: "",
                logical_operator: getLogicalOperatorForContext(false),
                filters: []
            };

            const updatedConditions = [...iteration.conditions, newCondition];
            const updatedIteration = {
                ...iteration,
                conditions: updateOuterConditionPriorities(updatedConditions)
            };

            return {
                ...prev,
                iterations: prev.iterations.map(i =>
                    i.id === iterationId ? updatedIteration : i
                )
            };
        });
    }, []);

    // Update an existing condition
    const updateCondition = useCallback((iterationId: string, conditionId: string, updates: Partial<OperationCondition>) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            const [condition, parentArray, index] = findCondition(iteration.conditions, conditionId);

            if (condition && condition.type === 'operations' && parentArray) {
                // Update the operation condition
                parentArray[index] = {
                    ...condition,
                    ...updates
                } as OperationCondition;

                return { ...prev };
            }

            return prev;
        });
    }, []);

    // Remove a condition
    const removeCondition = useCallback((iterationId: string, conditionId: string) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            const removeConditionRecursive = (conditions: ConditionNode[]): ConditionNode[] => {
                // Filter out the condition with the given ID
                const filtered = conditions.filter(c => c.id !== conditionId);

                // Recursively check nested conditions and remove empty parents
                return filtered
                    .map(c => {
                        if (c.type === 'condition') {
                            const nestedCondition = c as NestedCondition;
                            const updatedConditions = removeConditionRecursive(nestedCondition.conditions);

                            return {
                                ...c,
                                conditions: updatedConditions
                            };
                        }
                        return c;
                    })
                    .filter(c => {
                        // Remove nested conditions that have no sub-conditions left
                        if (c.type === 'condition') {
                            const nestedCondition = c as NestedCondition;
                            return nestedCondition.conditions.length > 0;
                        }
                        return true;
                    });
            };

            const updatedConditions = removeConditionRecursive(iteration.conditions);
            const reorderedConditions = updateOuterConditionPriorities(updatedConditions);

            return {
                ...prev,
                iterations: prev.iterations.map(i =>
                    i.id === iterationId
                        ? { ...i, conditions: reorderedConditions }
                        : i
                )
            };
        });
    }, []);

    // Reorder conditions within an iteration
    const reorderConditions = useCallback((iterationId: string, sourceIndex: number, destinationIndex: number) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            const conditions = [...iteration.conditions];
            const [removed] = conditions.splice(sourceIndex, 1);
            conditions.splice(destinationIndex, 0, removed);

            const reorderedConditions = updateOuterConditionPriorities(conditions);

            return {
                ...prev,
                iterations: prev.iterations.map(i =>
                    i.id === iterationId
                        ? { ...i, conditions: reorderedConditions }
                        : i
                )
            };
        });
    }, []);

    // Add a filter to a condition
    const addFilter = useCallback((iterationId: string, conditionId: string, filter: Partial<Filter>) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            const [condition, parentArray, index] = findCondition(iteration.conditions, conditionId);

            if (condition && condition.type === 'operations' && parentArray) {
                const operationCondition = condition as OperationCondition;
                const newFilter: Filter = {
                    id: `filter-${generateId()}`,
                    attribute_id: filter.attribute_id || "",
                    attribute_name: filter.attribute_name || "",
                    comparison_rule: filter.comparison_rule || "==",
                    attribute_value_id: filter.attribute_value_id,
                    attribute_value: filter.attribute_value || "",
                    logical_operator: filter.logical_operator
                };

                const updatedCondition = {
                    ...operationCondition,
                    filters: [...operationCondition.filters, newFilter]
                };

                parentArray[index] = updatedCondition;

                return { ...prev };
            }

            return prev;
        });
    }, []);

    // Update an existing filter
    const updateFilter = useCallback((iterationId: string, conditionId: string, filterId: string, updates: Partial<Filter>) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            const [condition, parentArray, index] = findCondition(iteration.conditions, conditionId);

            if (condition && condition.type === 'operations' && parentArray) {
                const operationCondition = condition as OperationCondition;
                const updatedFilters = operationCondition.filters.map(f =>
                    f.id === filterId ? { ...f, ...updates } : f
                );

                const updatedCondition = {
                    ...operationCondition,
                    filters: updatedFilters
                };

                parentArray[index] = updatedCondition;

                return { ...prev };
            }

            return prev;
        });
    }, []);

    // Remove a filter
    const removeFilter = useCallback((iterationId: string, conditionId: string, filterId: string) => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            const [condition, parentArray, index] = findCondition(iteration.conditions, conditionId);

            if (condition && condition.type === 'operations' && parentArray) {
                const operationCondition = condition as OperationCondition;
                const updatedFilters = operationCondition.filters.filter(f => f.id !== filterId);

                const updatedCondition = {
                    ...operationCondition,
                    filters: updatedFilters
                };

                parentArray[index] = updatedCondition;

                return { ...prev };
            }

            return prev;
        });
    }, []);

    // Find condition recursively and return its path
    const findACondition = (conditions: ConditionNode[], targetId: string, currentPath: number[] = []): { path: number[], found: boolean } => {
        for (let i = 0; i < conditions.length; i++) {
            const condition = conditions[i];
            const path = [...currentPath, i];

            if (condition.id === targetId) {
                return { path, found: true };
            }

            if (condition.type === 'condition') {
                const result = findACondition(condition.conditions, targetId, path);
                if (result.found) return result;
            }
        }
        return { path: [], found: false };
    };

    const indentCondition = (iterationId: string, conditionId: string, direction: 'left' | 'right') => {
        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            // Find the condition
            const result = findACondition(iteration.conditions, conditionId);
            if (!result.found) return prev;

            const path = result.path;
            let newConditions = JSON.parse(JSON.stringify(iteration.conditions)); // Deep clone

            // Get the condition to move
            let current = newConditions;
            for (let i = 0; i < path.length - 1; i++) {
                current = current[path[i]].conditions;
            }
            const conditionToMove = current[path[path.length - 1]];
            const sourceIndex = path[path.length - 1];

            if (direction === 'left') {
                // Can't move root level left
                if (path.length === 1) {
                    return prev;
                }

                // Remove from current position
                current.splice(sourceIndex, 1);

                // If the current array becomes empty, we need to clean up the parent
                if (current.length === 0) {
                    // Navigate to grandparent to remove the now-empty parent
                    let grandParentCurrent = newConditions;
                    for (let i = 0; i < path.length - 2; i++) {
                        grandParentCurrent = grandParentCurrent[path[i]].conditions;
                    }

                    // Remove the empty parent node
                    grandParentCurrent.splice(path[path.length - 2], 1);
                }

                // Calculate insertion index for parent level
                let parentCurrent = newConditions;
                for (let i = 0; i < path.length - 2; i++) {
                    parentCurrent = parentCurrent[path[i]].conditions;
                }

                // The parent condition index (where this condition is coming from)
                const parentConditionIndex = path[path.length - 2];

                // The source item's position within its immediate parent
                const sourceIndexInParent = sourceIndex;

                // Calculate insertion position based on source position
                // If source was at position 0 in nested condition, place before parent condition
                // If source was at higher positions, place after parent condition
                let insertionIndex;
                if (sourceIndexInParent === 0) {
                    insertionIndex = parentConditionIndex;
                } else {
                    insertionIndex = parentConditionIndex + 1;
                }

                // Ensure insertion index doesn't exceed array length
                const finalIndex = Math.min(insertionIndex, parentCurrent.length);
                parentCurrent.splice(finalIndex, 0, conditionToMove);
            }

            if (direction === 'right') {
                // Only operations can be moved right
                if (conditionToMove.type !== 'operations') {
                    console.log('Only operations can be moved right');
                    return prev;
                }

                // Check if there's an existing nested condition at the same level
                let existingNestedCondition = null;
                let existingIndex = -1;

                for (let i = 0; i < current.length; i++) {
                    if (i !== sourceIndex && current[i].type === 'condition') {
                        existingNestedCondition = current[i];
                        existingIndex = i;
                        break;
                    }
                }

                if (existingNestedCondition) {
                    // Create new operation with new ID and same data
                    const newOperation = {
                        ...conditionToMove,
                        id: generateId(),
                        priority: undefined // Inner conditions don't have priority
                    };

                    // Calculate insertion index based on position relationship
                    let insertionIndex;
                    if (sourceIndex > existingIndex) {
                        insertionIndex = current[existingIndex].conditions.length;
                    } else {
                        insertionIndex = 0;
                    }

                    // Insert at calculated position
                    current[existingIndex].conditions.splice(insertionIndex, 0, newOperation);

                    current.splice(sourceIndex, 1);
                } else {
                    // Create new operation with new ID and same data
                    const newOperation = {
                        ...conditionToMove,
                        id: generateId(),
                        priority: undefined // Inner conditions don't have priority
                    };

                    // Replace current condition with nested condition containing the new operation
                    const nestedCondition = {
                        id: conditionToMove.id,
                        type: 'condition' as const,
                        logical_operator: conditionToMove.logical_operator,
                        priority: conditionToMove.priority,
                        conditions: [newOperation]
                    };

                    current[sourceIndex] = nestedCondition;
                }
            }

            // Update priorities for outer conditions
            newConditions = updateOuterConditionPriorities(newConditions);

            return {
                ...prev,
                iterations: prev.iterations.map(iter =>
                    iter.id === iterationId
                        ? { ...iter, conditions: newConditions }
                        : iter
                )
            };
        });
    };
    const updateLogicalOperator = useCallback((
        iterationId: string,
        conditionId: string,
        operator: LogicalOperator
    ) => {

        setIterationPayload(prev => {
            if (!prev) return prev;

            const iteration = prev.iterations.find(i => i.id === iterationId);
            if (!iteration) return prev;

            // Recursive function to find and update the condition by ID
            const updateConditionById = (conditions: ConditionNode[]): ConditionNode[] => {
                return conditions.map(condition => {
                    // If this is the condition we're looking for, update its logical_operator
                    if (condition.id === conditionId) {
                        return {
                            ...condition,
                            logical_operator: operator
                        };
                    }

                    // If it's a nested condition, recursively search its sub-conditions
                    if (condition.type === 'condition') {
                        const nestedCondition = condition as NestedCondition;
                        return {
                            ...condition,
                            conditions: updateConditionById(nestedCondition.conditions)
                        };
                    }

                    // Return unchanged if not the target condition
                    return condition;
                });
            };

            const updatedConditions = updateConditionById(iteration.conditions);

            return {
                ...prev,
                iterations: prev.iterations.map(iter =>
                    iter.id === iterationId
                        ? { ...iter, conditions: updatedConditions }
                        : iter
                )
            };
        });
    }, []);

    return (
        <IterationContext.Provider value={{
            industryId,
            channelKey,
            createPermission,
            readPermission,
            updatePermission,
            deletePermission,
            activeTab,
            loading,
            onTabChange,
            fetchInitialState,
            resetState,
            iterationPayload,
            setIterationPayload,
            allCategories,
            handleCategoryChange,
            isEditMode,
            activeIterationId,
            currentIteration,
            activeIterationTab,
            addIteration,
            removeIteration,
            copyIteration,
            renameIteration,
            setActiveIterationId,
            setActiveIterationTab,
            reorderIterations,
            attributeList,
            addCondition,
            updateCondition,
            removeCondition,
            reorderConditions,
            addFilter,
            updateFilter,
            removeFilter,
            indentCondition,
            updateLogicalOperator,
            flattenConditions,
            originalIterationPayload,
            hasUnsavedChanges,
            showCancelDialog,
            setShowCancelDialog,
        }}>
            {children}
        </IterationContext.Provider>
    );
};